import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { dateTime } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Chips, Field, Modal, Notice, Textarea, useToast } from '../components/ui';
import type { BadgeTone } from '../components/ui';

type Review = 'PENDING' | 'APPROVED' | 'REJECTED';

interface MediaRow {
  id: string;
  kind: 'IMAGE' | 'VIDEO';
  url: string;
  thumbnailUrl: string | null;
  durationSec: number | null;
  status: 'PROCESSING' | 'READY' | 'FAILED';
  review: Review;
  reviewNote: string | null;
  processingError: string | null;
  createdAt: string;
  provider: { id: string; name: string; userId: string; customService: string | null; category: { name: string } };
}

const TONE: Record<Review, BadgeTone> = { PENDING: 'amber', APPROVED: 'green', REJECTED: 'red' };

function Preview({ m, large }: { m: MediaRow; large?: boolean }) {
  const size = large ? { width: '100%', maxHeight: 480 } : { width: 96, height: 72 };
  if (m.kind === 'VIDEO') {
    return large ? (
      <video src={m.url} poster={m.thumbnailUrl ?? undefined} controls preload="metadata" style={{ ...size, background: '#000', borderRadius: 8 }} />
    ) : (
      <div style={{ ...size, position: 'relative', borderRadius: 8, overflow: 'hidden', background: '#0f172a' }}>
        {m.thumbnailUrl && <img src={m.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
        <span style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#fff', fontSize: 26 }}>▶</span>
      </div>
    );
  }
  return <img src={m.url} alt="" style={{ ...size, objectFit: large ? 'contain' : 'cover', borderRadius: 8 }} />;
}

/**
 * Service providers' photos and videos: what is waiting for review, what is
 * shown, and what was turned down. Approve, reject with a reason, or remove
 * anything inappropriate outright.
 */
export function ProviderMediaPage() {
  const { get, set } = useListParams();
  const review = (get('review') || 'PENDING') as Review | 'ALL';
  const providerId = get('providerId');
  const [open, setOpen] = useState<MediaRow | null>(null);
  const [note, setNote] = useState('');
  const toast = useToast();
  const qc = useQueryClient();

  const act = useMutation({
    mutationFn: ({ kind, id }: { kind: 'approve' | 'reject' | 'remove'; id: string }) =>
      kind === 'remove'
        ? api.del(`/admin/provider-media/${id}`)
        : api.post(`/admin/provider-media/${id}/${kind}`, kind === 'reject' ? { note } : undefined),
    onSuccess: (_, { kind }) => {
      toast(kind === 'approve' ? 'Approved: customers can see it' : kind === 'reject' ? 'Rejected' : 'Removed', 'success');
      setOpen(null);
      setNote('');
      qc.invalidateQueries({ queryKey: ['provider-media'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<MediaRow>> = [
    { key: 'preview', header: '', render: (m) => <Preview m={m} /> },
    {
      key: 'provider',
      header: 'Provider',
      render: (m) => (
        <div>
          <div className="cell-strong">{m.provider.name}</div>
          <div className="cell-muted">
            {m.provider.customService ?? m.provider.category.name} ·{' '}
            <Link to={`/provider-media?providerId=${m.provider.id}&review=ALL`} onClick={(e) => e.stopPropagation()}>
              all their media
            </Link>
          </div>
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Type',
      render: (m) => (m.kind === 'VIDEO' ? `Video${m.durationSec && m.durationSec > 1 ? ` · ${Math.floor(m.durationSec / 60)}:${String(m.durationSec % 60).padStart(2, '0')}` : ''}` : 'Photo'),
    },
    {
      key: 'review',
      header: 'Review',
      render: (m) => (
        <div>
          <Badge tone={TONE[m.review]}>{m.review === 'PENDING' ? 'Waiting' : m.review === 'APPROVED' ? 'Shown' : 'Rejected'}</Badge>
          {m.status !== 'READY' && <div className="cell-muted">{m.status === 'FAILED' ? (m.processingError ?? 'Failed') : 'Processing'}</div>}
          {m.reviewNote && <div className="cell-muted">{m.reviewNote}</div>}
        </div>
      ),
    },
    { key: 'when', header: 'Uploaded', render: (m) => <span className="cell-muted">{dateTime(m.createdAt)}</span> },
  ];

  return (
    <div className="stack">
      <Notice tone="info" title="Customers see a provider's photo or video only once it is approved here">
        Rejecting tells the provider why and keeps it off their page; removing deletes it for good. Both are recorded.
        {providerId && (
          <>
            {' '}Showing one provider. <Link to="/provider-media">Show everyone</Link>
          </>
        )}
      </Notice>
      <DataTable<MediaRow>
        queryKey={['provider-media', review, providerId]}
        path="/admin/provider-media"
        params={{ review: review === 'ALL' ? '' : review, providerId }}
        columns={columns}
        onRowClick={(m) => setOpen(m)}
        emptyIcon="🖼"
        emptyTitle={review === 'PENDING' ? 'Nothing waiting for review' : 'Nothing here'}
        toolbar={
          <Chips
            value={review}
            onChange={(v) => set('review', v === 'PENDING' ? '' : v)}
            options={[
              { value: 'PENDING', label: 'Waiting for review' },
              { value: 'APPROVED', label: 'Shown' },
              { value: 'REJECTED', label: 'Rejected' },
              { value: 'ALL', label: 'All' },
            ]}
          />
        }
      />

      {open && (
        <Modal
          title={`${open.kind === 'VIDEO' ? 'Video' : 'Photo'} from ${open.provider.name}`}
          sub={`Uploaded ${dateTime(open.createdAt)}`}
          onClose={() => setOpen(null)}
          wide
          footer={
            <>
              <Button variant="danger" loading={act.isPending} onClick={() => act.mutate({ kind: 'remove', id: open.id })}>
                Remove
              </Button>
              <Button loading={act.isPending} disabled={note.trim().length < 3} onClick={() => act.mutate({ kind: 'reject', id: open.id })}>
                Reject
              </Button>
              <Button variant="success" loading={act.isPending} disabled={open.review === 'APPROVED' || open.status !== 'READY'} onClick={() => act.mutate({ kind: 'approve', id: open.id })}>
                Approve
              </Button>
            </>
          }
        >
          <div className="stack">
            <Preview m={open} large />
            <Field label="Reason" hint="Needed to reject. The provider sees it.">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Shows a phone number" />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}
